import { useCallback, useEffect, useMemo } from 'react'

import { create } from 'zustand'

import { useCanEnableSharepoint } from '@/hooks/sharepoint/useCanEnableSharepoint'
import { useHasSharepointIntegration } from '@/hooks/sharepoint/useSharepointIntegration'

import type { UUID } from '@/types/Id'

type Status =
  | 'not_allowed' // user does not have permissions to connect to sharepoint
  | 'idle' // user never clicked "Connect"
  | 'connecting' // OAuth popup open
  | 'waiting' // OAuth finished; waiting for token storage
  | 'ready' // user has an active MCP token

interface UseSharepointSetupStatusProps {
  orgId: UUID | ''
}

type SharepointSetupStatusResponse = {
  status: Status
  setStatus: (s: Status) => void
}

// Only store transient states in Zustand
interface SharepointSetupStore {
  transientStatus: 'waiting' | 'connecting' | null
  setTransientStatus: (s: 'waiting' | 'connecting' | null) => void
}

const useSharepointSetupStore = create<SharepointSetupStore>((set) => ({
  transientStatus: null,
  setTransientStatus: (s) => set({ transientStatus: s }),
}))

export const useSharepointSetupStatus = ({
  orgId,
}: UseSharepointSetupStatusProps): SharepointSetupStatusResponse => {
  const { transientStatus, setTransientStatus } = useSharepointSetupStore()

  const { data: hasToken = false } = useHasSharepointIntegration({ orgId })

  const canEnableSharepoint = useCanEnableSharepoint()

  const baseStatus = useMemo<Status>(() => {
    if (!canEnableSharepoint) return 'not_allowed'
    if (hasToken) return 'ready'
    return 'idle'
  }, [canEnableSharepoint, hasToken])

  // Final status: transient states override base status
  const status = transientStatus || baseStatus

  // Clear transient status when token is confirmed
  useEffect(() => {
    if (transientStatus === 'waiting' && hasToken) {
      setTransientStatus(null) // Will fall back to 'ready' from baseStatus
    }
  }, [transientStatus, hasToken, setTransientStatus])

  const setStatus = useCallback(
    (s: Status) => {
      if (s === 'waiting' || s === 'connecting') {
        setTransientStatus(s)
      } else {
        setTransientStatus(null)
      }
    },
    [setTransientStatus],
  )

  return { status, setStatus }
}
